/**
 * The Overview tab (§10.3): the enrichment summaries, the coverage matrix at
 * full size, what was most recently filed, and a short ledger of what the
 * pipeline actually extracted.
 */
import { useState } from 'react'
import type { CompanyDetail } from '@server/sql/companies.ts'
import { Field, Label, NotRecorded, Panel } from '~/components/ui/primitives.tsx'
import { Raw } from '~/components/ui/raw.tsx'
import { useT } from '~/i18n/index.tsx'
import { count, date } from '~/i18n/format.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'
import { CoverageMatrix } from './coverage.tsx'

export function CompanyOverview({ company }: { company: CompanyDetail }) {
  const { t, locale, v } = useT()
  const superadmin = useSuperadmin()
  const [showOther, setShowOther] = useState(false)

  // The summary is shown in the reading language, with the other available
  // on a toggle -- and the toggle only appears when there is another.
  const primary = locale === 'ar' ? company.businessSummaryAr : company.businessSummaryEn
  const other = locale === 'ar' ? company.businessSummaryEn : company.businessSummaryAr
  const otherLang = locale === 'ar' ? 'en' : 'ar'
  const summary = showOther ? other : (primary ?? other)
  const summaryLang = showOther ? otherLang : (primary ? locale : otherLang)

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <div className="space-y-5">
        {summary && (
          <section>
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <Label>{t('company.summary')}</Label>
              {other && (
                <button
                  type="button"
                  onClick={() => setShowOther((s) => !s)}
                  className="relative tap inline-flex min-h-[var(--control-h-sm)] items-center text-[length:var(--text-micro)] link-inline"
                >
                  {t('company.summary.other', {
                    lang: showOther
                      ? (v('locale', locale) ?? locale)
                      : (v('locale', otherLang) ?? otherLang),
                  })}
                </button>
              )}
            </div>
            <Raw
              as="p"
              lang={summaryLang}
              className="max-w-[76ch] text-[length:var(--text-prose)] leading-[var(--text-prose--line-height)] text-[var(--text)]"
            >
              {summary}
            </Raw>
          </section>
        )}

        <section>
          <Label className="mb-1.5">{t('results.coverage')}</Label>
          <Panel className="inline-block px-3 py-2.5">
            <CoverageMatrix symbol={company.symbol} coverage={company.coverage} size="md" />
          </Panel>
          <p className="mt-1 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {t('results.coverageLegend')}
          </p>
        </section>
      </div>

      <div className="space-y-5">
        {/* --- the extraction ledger ---------------------------------- */}
        <section>
          <Label className="mb-1.5">{t('company.ledger')}</Label>
          <Panel className="divide-y divide-[var(--border)]">
            <LedgerRow label={t('company.ledger.facts')} n={company.counts.facts} />
            <LedgerRow label={t('company.ledger.tables')} n={company.counts.tables} />
            <LedgerRow label={t('company.ledger.notes')} n={company.counts.notes} />
            {superadmin && <LedgerRow label={t('company.ledger.checks')} n={company.counts.checks} />}
          </Panel>
        </section>

        {/* --- as most recently filed --------------------------------- */}
        <section>
          <Panel className="grid grid-cols-2 gap-3 px-3 py-2.5">
            <Field label={t('company.currency')}>
              {company.currency ?? <NotRecorded>{t('common.notRecorded')}</NotRecorded>}
            </Field>
            <Field label={t('company.rounding')}>
              {company.rounding ?? <NotRecorded>{t('common.notRecorded')}</NotRecorded>}
            </Field>
          </Panel>
        </section>

        {company.latestAuditor && (
          <section>
            <Label className="mb-1.5">{t('company.latestAuditor')}</Label>
            <Panel className="space-y-2 px-3 py-2.5">
              <Raw lang={company.latestAuditor.lang} className="block text-[length:var(--text-data)]">
                {company.latestAuditor.firmNameRaw ?? '—'}
              </Raw>
              {company.latestAuditor.signerNameRaw && (
                <Raw
                  lang={company.latestAuditor.lang}
                  className="block text-[length:var(--text-tiny)] text-[var(--text-muted)]"
                >
                  {company.latestAuditor.signerNameRaw}
                </Raw>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {company.latestAuditor.opinionType && (
                  <Field label={t('company.opinion')}>
                    <Raw lang={company.latestAuditor.lang}>{company.latestAuditor.opinionType}</Raw>
                  </Field>
                )}
                {/* Gregorian formatted; the Hijri string is shown exactly as
                    printed, unparsed (§11.3). */}
                {company.latestAuditor.reportDateG && (
                  <Field label="Gregorian">{date(company.latestAuditor.reportDateG, locale)}</Field>
                )}
                {company.latestAuditor.reportDateHRaw && (
                  <Field label="Hijri">
                    <Raw lang="ar">{company.latestAuditor.reportDateHRaw}</Raw>
                  </Field>
                )}
              </div>
            </Panel>
          </section>
        )}
      </div>
    </div>
  )
}

function LedgerRow({ label, n }: { label: string; n: number }) {
  const { locale } = useT()
  return (
    <div className="flex items-baseline justify-between px-3 py-1.5">
      <span className="text-[length:var(--text-data)] text-[var(--text-muted)]">{label}</span>
      <span className="tnum text-[length:var(--text-data)] font-medium text-[var(--text)]">
        {count(n, locale)}
      </span>
    </div>
  )
}
